import classNames from "classnames";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ViewProps } from "../ExecutiveDashboard";
import Hint, { InfoDot } from "../components/Hint";
import { Card, Empty, ErrorState, Kpi, Loading, PendingTag, Ring, StagePill, StatCircle } from "../components/Visuals";
import { area, areaPercent, areaRatio, bothAreas, date, LEAD_UNIT, NEXT_STEP, number, otherUnit, percent, plural, subtractArea, TRADING_RULE } from "../format";
import { InFlightRow, OverviewPipelineResponse, OverviewResponse, StageCounts } from "../types";
import { useDashboardData } from "../useDashboardData";

type Bucket = "trading" | "design" | "fitout" | "vacant";

const BUCKET_VIEWS: { [key in Bucket]: [string, { [name: string]: string }?] } = {
	trading: ["tenancy-schedule", { status: "trading" }],
	design: ["design-stage"],
	fitout: ["fitout-stage"],
	vacant: ["vacant-units"]
};

const BUCKETS: { key: Bucket; label: string; tone: "trading" | "design" | "fitout" | "vacant"; rule: string; byLease: boolean }[] = [
	{
		key: "trading",
		label: "Total Trading",
		tone: "trading",
		rule: TRADING_RULE,
		byLease: true
	},
	{
		key: "design",
		label: "Design Stage",
		tone: "design",
		rule: "Leases signed but not open, before the Actual Move in Date on the Site Handover task.",
		byLease: true
	},
	{
		key: "fitout",
		label: "Fitout Stage",
		tone: "fitout",
		rule: "Leases signed but not open, on or after the Actual Move in Date on the Site Handover task.",
		byLease: true
	},
	{
		key: "vacant",
		label: "Vacant",
		tone: "vacant",
		rule: "Units with no lease today: empty, terminated or moved out. Signed units in Design or Fit-out are not included.",
		byLease: false
	}
];

const Overview = ({ asset, assetHref, viewHref }: ViewProps) => {
	const bucketHref = (bucket: Bucket) => viewHref(...BUCKET_VIEWS[bucket]);
	const { data, isLoading, error } = useDashboardData<OverviewResponse>("overview", { property_id: asset ? asset.id : undefined });
	const pipeline = useDashboardData<OverviewPipelineResponse>("overview_pipeline", { property_id: asset ? asset.id : undefined }, { background: true });

	if (isLoading) return <Loading />;
	if (error || !data) return <ErrorState />;

	const { units, gla, refurbishment } = data;
	const design = pipeline.data ? pipeline.data.design : NO_COUNTS;
	const fitout = pipeline.data ? pipeline.data.fitout : NO_COUNTS;
	const stageNumber = (counts: StageCounts) => (pipeline.data ? number(counts.projects) : "…");
	const unit = LEAD_UNIT;
	const both = (value: typeof gla.total) => bothAreas(value, unit);
	const second = (value: typeof gla.total) => area(value, otherUnit(unit));
	const scopeLabel = asset ? "at this asset" : "across portfolio";

	return (
		<div className="ed-stack">
			<Card
				eyebrow="Occupancy by units"
				title={
					<React.Fragment>
						{number(units.total)} <span className="ed-muted">{scopeLabel} ({plural(units.units.total, "unit")})</span>
					</React.Fragment>
				}
				tag="Exclusive · sums to 100%"
				explain={{
					title: "Occupancy by units",
					lines: [
						"Source: Yardi units and leases, as of today.",
						"Trading, Design and Fit-out count leases (a lease over several units counts once); Vacant counts individual units.",
						`${number(units.trading)} + ${number(units.design)} + ${number(units.fitout)} + ${number(units.vacant)} = ${number(units.total)} (${plural(units.units.total, "unit")}).`,
						"Units Yardi has excluded or flagged removed are not counted."
					]
				}}
			>
				<div className="ed-stat-row ed-stat-row--tall">
					<StatCircle
						value={number(units.total)}
						caption="100.0%"
						label="Total"
						sublabel={`(${plural(units.units.total, "unit")})`}
						tone="dark"
						to={viewHref("tenancy-schedule")}
						explain={{
							title: "Total",
							lines: [
								`${number(units.trading)} trading + ${number(units.design)} design + ${number(units.fitout)} fit-out + ${number(units.vacant)} vacant = ${number(units.total)}`,
								`${plural(units.units.total, "leasable unit")} in total.`,
								"The denominator for the percentages."
							]
						}}
					/>
					{BUCKETS.map((bucket) => (
						<StatCircle
							key={bucket.key}
							value={number(units[bucket.key])}
							caption={percent(units[bucket.key], units.total, 1)}
							label={bucket.label}
							sublabel={`(${plural(units.units[bucket.key], "unit")})`}
							tone={bucket.tone}
							size={bucket.key === "trading" ? "lg" : "md"}
							to={bucketHref(bucket.key)}
							explain={{
								title: bucket.label,
								lines: [
									bucket.rule,
									bucket.byLease ? "Counted by lease: a lease over several units counts once." : "Counted per unit.",
									`${number(units[bucket.key])} ÷ ${number(units.total)} total = ${percent(units[bucket.key], units.total)}`,
									`${plural(units.units[bucket.key], "unit")}.`
								]
							}}
						/>
					))}
				</div>
				<div className="ed-divider" />
				<div className="ed-eyebrow">
					Occupancy by area (GLA)
					<InfoDot
						align="left"
						explain={{
							title: "Occupancy by area",
							lines: [
								"Source: Lease Area SQFT and SQM from Yardi unit master data, as in force today.",
								"Where a unit has no measurement, the unit area on its lease amendment is used, converted at 1 sqm = 10.7639 sqft.",
								"Trading + Design + Fit-out + Vacant = Total GLA.",
								gla.units_without_area > 0 && `${plural(gla.units_without_area, "unit")} have no area recorded and add 0.`
							]
						}}
					/>
				</div>
				<div className="ed-stat-row">
					<Ring
						fraction={1}
						label="100%"
						amount={area(gla.total, unit)}
						secondary={second(gla.total)}
						caption="Total GLA"
						tone="dark"
						to={viewHref("tenancy-schedule")}
						explain={{ title: "Total GLA", lines: ["Lease area of every counted unit.", both(gla.total)] }}
					/>
					{BUCKETS.map((bucket) => (
						<Ring
							key={bucket.key}
							fraction={areaRatio(gla[bucket.key], gla.total)}
							label={areaPercent(gla[bucket.key], gla.total)}
							amount={area(gla[bucket.key], unit)}
							secondary={second(gla[bucket.key])}
							caption={bucket.label}
							tone={bucket.tone}
							to={bucketHref(bucket.key)}
							explain={{
								title: `${bucket.label} (by area)`,
								lines: [`Lease area of the ${bucket.label.toLowerCase()} units.`, `${both(gla[bucket.key])} ÷ ${both(gla.total)} = ${areaPercent(gla[bucket.key], gla.total)}`]
							}}
						/>
					))}
				</div>
			</Card>

			<Card
				eyebrow="Workflow activity"
				tag="Overlapping · not in occupancy %"
				explain={{
					title: "Workflow activity",
					lines: ["Source: TCPinpoint workflows.", "Counts of work in motion. They are never added to or taken away from Trading."]
				}}
			>
				<p className="ed-note">
					These are overlapping workflow counts of projects, never folded into the occupancy %. <strong>In Design</strong> and{" "}
					<strong>In Fit-out</strong> are new tenancies on their way to opening, including ones taking over units another tenant is still
					trading in; <strong>In Refurbishment</strong> runs on still-trading units, which stay in Trading.
				</p>
				<div className="ed-tiles">
					<Hint
						block
						className="ed-tile"
						explain={{
							title: "In Design",
							lines: [
								"Opening projects (Original Lease, Expansion, Relocation) not yet open, before the Actual Move in Date on the Site Handover task.",
								pipeline.data && `${plural(design.projects, "project")} over ${plural(design.units, "unit")}.`,
								design.pending > 0 && `${plural(design.pending, "project")} still pending (not scheduled yet).`,
								"Includes incoming tenancies on units another tenant is still trading in.",
								NEXT_STEP.design
							]
						}}
					>
						<span className="ed-tile__number ed-tone--design">{stageNumber(design)}</span>
						<span>
							<span className="ed-tile__title">
								In Design <span className="ed-muted">· projects{pipeline.data && ` (${plural(design.units, "unit")})`}</span>
							</span>
							<span className="ed-tile__text">{pendingNote(design, "New tenancies in design, opening pipeline")}</span>
						</span>
					</Hint>
					<Hint
						block
						className="ed-tile"
						explain={{
							title: "In Fit-out",
							lines: [
								"Opening projects not yet open, on or after the Actual Move in Date on the Site Handover task.",
								pipeline.data && `${plural(fitout.projects, "project")} over ${plural(fitout.units, "unit")}.`,
								fitout.pending > 0 && `${plural(fitout.pending, "project")} still pending (not scheduled yet).`,
								NEXT_STEP.fitout
							]
						}}
					>
						<span className="ed-tile__number ed-tone--fitout">{stageNumber(fitout)}</span>
						<span>
							<span className="ed-tile__title">
								In Fit-out <span className="ed-muted">· projects{pipeline.data && ` (${plural(fitout.units, "unit")})`}</span>
							</span>
							<span className="ed-tile__text">Tenancies on site in fit-out, pre-opening, not trading</span>
						</span>
					</Hint>
					<Hint
						block
						className="ed-tile"
						explain={{
							title: "In Refurbishment",
							lines: [
								"Active projects for Renewal amendments of type Major or Minor Refurbishment.",
								`${number(refurbishment.on_trading_units)} of them are on units that are all still trading.`
							]
						}}
					>
						<span className="ed-tile__number ed-tone--refurb">{number(refurbishment.projects)}</span>
						<span>
							<span className="ed-tile__title">
								In Refurbishment <span className="ed-muted">· active</span>
							</span>
							<span className="ed-tile__text">
								{number(refurbishment.on_trading_units)} running on still-trading units (kept in Trading)
							</span>
						</span>
					</Hint>
				</div>
			</Card>

			<div className="ed-strip">
				<Kpi
					label="Total GLA"
					value={area(gla.total, unit)}
					sub={`${second(gla.total)} · Gross lettable area`}
					explain={{ title: "Total GLA", lines: ["Lease area of every counted unit.", both(gla.total), "The denominator for both percentages in this strip."] }}
				/>
				<Kpi
					label="Leased GLA"
					value={area(gla.leased, unit)}
					sub={`${second(gla.leased)} · Signed leases, all stages`}
					explain={{
						title: "Leased GLA",
						lines: [
							"Area under a lease at any stage: trading, plus signed but not yet open.",
							`${area(gla.trading, unit)} trading + ${area(gla.design, unit)} in Design + ${area(gla.fitout, unit)} in Fit-out = ${area(gla.leased, unit)}`,
							both(gla.leased)
						]
					}}
				/>
				<Kpi
					label="Trading (by area)"
					value={areaPercent(gla.trading, gla.total)}
					valueTone="trading"
					sub="Open & operating ÷ total GLA"
					explain={{ title: "Trading (by area)", lines: [`${both(gla.trading)} ÷ ${both(gla.total)} = ${areaPercent(gla.trading, gla.total)}`] }}
				/>
				<Kpi
					label="Occupancy (by area)"
					value={areaPercent(gla.leased, gla.total)}
					valueTone="design"
					sub="Leased GLA ÷ total GLA"
					explain={{
						title: "Occupancy (by area)",
						lines: [
							`${both(gla.leased)} ÷ ${both(gla.total)} = ${areaPercent(gla.leased, gla.total)}`,
							`The gap to Trading (${both(subtractArea(gla.leased, gla.trading))}) is leased space not yet open.`
						]
					}}
				/>
			</div>

			{!asset && (
				<Card title="Status by property" subtitle={`${plural(data.properties.length, "asset")} in scope`} className="ed-card--flush">
					<div className="ed-table-wrap">
						<table className="ed-table">
							<thead>
								<tr>
									<th>Property</th>
									<th className="ed-num">Total</th>
									<th className="ed-num">Trading</th>
									<th className="ed-num ed-col--pipeline">
										Design
										<InfoDot align="right" explain={{ title: "Design", lines: [BUCKETS[1].rule, "Leases, with their units in brackets."] }} />
									</th>
									<th className="ed-num ed-col--pipeline">
										Fit-out
										<InfoDot align="right" explain={{ title: "Fit-out", lines: [BUCKETS[2].rule, "Leases, with their units in brackets."] }} />
									</th>
									<th className="ed-num">Vacant</th>
									<th className="ed-num">
										Occupancy
										<InfoDot align="right" explain={{ title: "Occupancy", lines: ["Trading ÷ Total at the property.", "Total = Trading + Design + Fit-out + Vacant. Unit counts are in brackets."] }} />
									</th>
								</tr>
							</thead>
							<tbody>
								{data.properties.map((row) => (
									<tr key={row.id}>
										<td>
											<Link to={assetHref(row.id)} className="ed-link">
												{row.name}
											</Link>
											<div className="ed-subtext">{row.location}</div>
										</td>
										<td className="ed-num">
											{number(row.units.total)} <span className="ed-muted">({number(row.units.units.total)})</span>
										</td>
										<td className="ed-num ed-text--trading">
											<Hint align="right" explain={{ lines: [`${plural(row.units.trading, "trading lease")} over ${plural(row.units.units.trading, "unit")}.`] }}>
												{number(row.units.trading)} <span className="ed-muted">({number(row.units.units.trading)})</span>
											</Hint>
										</td>
										<td className="ed-num ed-col--pipeline ed-text--design">
											<Hint align="right" explain={{ lines: [`${plural(row.units.design, "lease")} in Design over ${plural(row.units.units.design, "unit")}.`] }}>
												<span className="ed-strong">{number(row.units.design)}</span> <span className="ed-muted">({number(row.units.units.design)})</span>
											</Hint>
										</td>
										<td className="ed-num ed-col--pipeline ed-text--fitout">
											<Hint align="right" explain={{ lines: [`${plural(row.units.fitout, "lease")} in Fit-out over ${plural(row.units.units.fitout, "unit")}.`] }}>
												<span className="ed-strong">{number(row.units.fitout)}</span> <span className="ed-muted">({number(row.units.units.fitout)})</span>
											</Hint>
										</td>
										<td className="ed-num ed-text--vacant">
											<Hint align="right" explain={{ lines: [BUCKETS[3].rule] }}>{number(row.units.vacant)}</Hint>
										</td>
										<td className="ed-num ed-strong">
											<Hint align="right" explain={{ lines: [`${number(row.units.trading)} trading ÷ ${number(row.units.total)} total`] }}>
												{row.units.total > 0 ? percent(row.units.trading, row.units.total) : "No units"}
											</Hint>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Card>
			)}

			{pipeline.data ? (
				<InFlightTable rows={pipeline.data.in_flight} showProperty={!asset} />
			) : (
				<Card title="In-flight units · Design & Fit-out" className="ed-card--flush">
					{pipeline.error ? <ErrorState /> : <Loading />}
				</Card>
			)}
		</div>
	);
};

const STAGE_LABELS = { design: "Design Stage", fitout: "Fitout Stage" };

const InFlightTable = ({ rows, showProperty }: { rows: InFlightRow[]; showProperty: boolean }) => {
	const [zone, setZone] = useState("");
	const [subZone, setSubZone] = useState("");
	const [stage, setStage] = useState("");
	const [query, setQuery] = useState("");

	const zones = useMemo(() => uniqueValues(rows.map((row) => row.zone)), [rows]);
	const subZones = useMemo(() => uniqueValues(rows.map((row) => row.sub_zone)), [rows]);
	const needle = query.trim().toLowerCase();
	const filtered = rows.filter(
		(row) =>
			(!zone || row.zone === zone) &&
			(!subZone || row.sub_zone === subZone) &&
			(!stage || row.stage === stage) &&
			(!needle ||
				[row.tenant_name, row.units.join(" "), row.property.short_name, row.property.name, row.zone, row.sub_zone]
					.join(" ")
					.toLowerCase()
					.includes(needle))
	);

	return (
		<Card
			title="In-flight units · Design & Fit-out"
			subtitle={`${plural(filtered.length, "project")} · ${plural(filtered.reduce((sum, row) => sum + row.units.length, 0), "unit")} moving toward opening${
				filtered.some((row) => row.pending) ? ` · ${number(filtered.filter((row) => row.pending).length)} pending` : ""
			}`}
			className="ed-card--flush"
		>
			<div className="ed-card__toolbar">
				<input
					className="ed-search"
					type="search"
					placeholder="Search tenant, unit, zone…"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				<Select label="Zone" value={zone} options={zones} onChange={setZone} />
				<Select label="Sub zone" value={subZone} options={subZones} onChange={setSubZone} />
				<Select
					label="Stage"
					value={stage}
					options={["design", "fitout"]}
					format={(value) => STAGE_LABELS[value as "design" | "fitout"]}
					onChange={setStage}
				/>
			</div>

			{filtered.length === 0 ? (
				<Empty>No projects in Design or Fit-out match these filters.</Empty>
			) : (
				<div className="ed-table-wrap">
					<table className="ed-table">
						<thead>
							<tr>
								<th className="ed-num ed-col--index">#</th>
								<th>Tenant</th>
								{showProperty && <th>Property</th>}
								<th>Unit</th>
								<th>Zone</th>
								<th>Sub zone</th>
								<th className="ed-num">GLA</th>
								<th>Stage</th>
								<th>
									Target open
									<InfoDot align="right" explain={{ lines: ["Actual opening date if recorded, otherwise the agreed opening date from Yardi.", "The list is sorted by this date."] }} />
								</th>
							</tr>
						</thead>
						<tbody>
							{filtered.map((row, index) => (
								<tr key={row.key}>
									<td className="ed-num ed-col--index">{index + 1}</td>
									<td className="ed-strong">
										{row.project_id ? (
											<Link
												to={`/projects/${row.project_id}`}
												target="_blank"
												rel="noopener noreferrer"
												className="ed-link"
												title="Open project in a new tab"
											>
												{row.tenant_name || "Unnamed tenant"}
											</Link>
										) : (
											row.tenant_name || "Unnamed tenant"
										)}
										{row.pending && <PendingTag />}
									</td>
									{showProperty && <td>{row.property.short_name}</td>}
									<td>{row.units.join(", ")}</td>
									<td>{row.zone || "–"}</td>
									<td>{row.sub_zone || "–"}</td>
									<td className="ed-num">
										{area(row.gla)}
										<div className="ed-subtext">{area(row.gla, otherUnit(LEAD_UNIT))}</div>
									</td>
									<td>
										<Hint
											explain={{
												lines: [
													row.stage === "design" ? "Before the Actual Move in Date." : "On or after the Actual Move in Date, not yet open.",
													row.handover_actual ? `Actual Move in Date: ${date(row.handover_date)}` : `Agreed Site Handover: ${date(row.handover_date)}`,
													NEXT_STEP[row.stage]
												]
											}}
										>
											<StagePill stage={row.stage} label={STAGE_LABELS[row.stage]} />
										</Hint>
									</td>
									<td className={classNames({ "ed-strong": row.target_open_actual })}>{date(row.target_open_date)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</Card>
	);
};

const NO_COUNTS: StageCounts = { projects: 0, units: 0, pending: 0 };

const pendingNote = (counts: StageCounts, fallback: string) =>
	counts.pending > 0 ? `${number(counts.pending)} of ${number(counts.projects)} pending (not scheduled yet)` : fallback;

const uniqueValues = (values: (string | null)[]) =>
	Array.from(new Set(values.filter((value): value is string => !!value))).sort();

interface SelectProps {
	label: string;
	value: string;
	options: string[];
	onChange: (value: string) => void;
	format?: (value: string) => string;
}

export const Select = ({ label, value, options, onChange, format = (option) => option }: SelectProps) => (
	<label className="ed-select">
		<span className="ed-select__label">{label}:</span>
		<select value={value} onChange={(event) => onChange(event.target.value)}>
			<option value="">All</option>
			{options.map((option) => (
				<option key={option} value={option}>
					{format(option)}
				</option>
			))}
		</select>
	</label>
);

export default Overview;
